<template>
  <div class="sv2-page">
    <!-- Hero -->
    <header class="sv2-hero">
      <h1>Our Journey</h1>
      <p>温柔地足迹我们的日子，将记忆编织在一起。</p>
    </header>

    <!-- Mobile Tab Switch -->
    <div v-if="isCompactMobile && !isVisitor" class="sv2-mob-tabs">
      <button :class="{ active: mobileSection === 'timeline' }" @click="mobileSection = 'timeline'">时间轴</button>
      <button :class="{ active: mobileSection === 'anniversary' }" @click="mobileSection = 'anniversary'">纪念日</button>
      <button :class="{ active: mobileSection === 'whisper' }" @click="mobileSection = 'whisper'">悄悄话</button>
    </div>

    <!-- Bento Grid -->
    <div class="sv2-grid">
      <!-- ===== Left: Timeline ===== -->
      <div v-show="!isCompactMobile || mobileSection === 'timeline'" class="sv2-timeline-col">
        <div v-if="!isVisitor" class="sv2-add-row">
          <button class="sv2-add-btn" @click="openCreateDialog">
            <span class="material-symbols-outlined" style="font-size:18px">add</span>
            添加新纪录
          </button>
        </div>

        <section v-loading="loadingStories" class="sv2-tl-section">
          <div v-if="stories.length" ref="timelineRef" class="sv2-tl">
            <div class="sv2-tl-line"></div>
            <div
              v-for="group in groupedStories"
              :key="group.date"
              class="sv2-tl-item"
            >
              <div class="sv2-tl-node"></div>
              <div class="sv2-tl-body">
                <div
                  v-for="story in group.stories"
                  :key="story.id"
                  class="sv2-tl-card"
                  @click="openStoryDetail(story.id)"
                >
                  <div class="sv2-tl-card__glow"></div>
                  <span class="sv2-tl-date">{{ formatDateCn(group.date) }}</span>
                  <h3 class="sv2-tl-title">{{ story.title }}</h3>
                  <p v-if="story.description" class="sv2-tl-desc">{{ story.description }}</p>
                  <div v-if="story.coverImage" class="sv2-tl-media">
                    <img :src="story.coverImage" alt="">
                  </div>
                  <div v-if="story.location || story.mood || story.weather" class="sv2-tl-tags">
                    <span v-if="story.location" class="sv2-tag sv2-tag--loc">{{ story.location }}</span>
                    <span v-if="story.mood" class="sv2-tag sv2-tag--mood">{{ story.mood }}</span>
                    <span v-if="story.weather" class="sv2-tag sv2-tag--weather">{{ story.weather }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <el-empty v-else description="" />
        </section>

        <div v-if="pagination.total > stories.length" ref="loadMoreRef" class="sv2-load-more">
          <span class="sv2-load-dot"></span>
          <span class="sv2-load-dot"></span>
          <span class="sv2-load-dot"></span>
        </div>
        <div v-else-if="stories.length" class="sv2-load-end">到底啦 ♡</div>
      </div>

      <!-- ===== Right: Sidebar ===== -->
      <div v-if="!isVisitor" v-show="!isCompactMobile || mobileSection !== 'timeline'" class="sv2-sidebar-col">
        <!-- Milestones -->
        <section v-show="!isCompactMobile || mobileSection === 'anniversary'" class="sv2-ms-section">
          <div class="sv2-ms-head">
            <h2><span class="material-symbols-outlined">calendar_today</span> 纪念日</h2>
            <button class="sv2-ms-add" @click="openAnniDialog()">
              <span class="material-symbols-outlined" style="font-size:16px">add</span> 新增
            </button>
          </div>
          <div v-loading="loadingAnni" class="sv2-ms-list">
            <div
              v-for="item in anniversaryList"
              :key="item.id || item.title"
              class="sv2-ms-card"
              :class="{ 'sv2-ms-card--primary': item.display.tone === 'upcoming' || item.display.tone === 'today' }"
            >
              <div class="sv2-ms-card__icon">
                <span class="material-symbols-outlined">{{ item.display.tone === 'today' ? 'celebration' : 'favorite' }}</span>
              </div>
              <p class="sv2-ms-card__label">{{ item.title }}</p>
              <div class="sv2-ms-card__count">
                <span class="sv2-ms-card__num">{{ item.display.days }}</span>
                <span class="sv2-ms-card__unit">天</span>
              </div>
              <p class="sv2-ms-card__date">{{ item.display.startDateText }}</p>
              <div class="sv2-ms-card__actions">
                <button @click.stop="openAnniDialog(item)">编辑</button>
                <el-popconfirm title="确认删除？" @confirm="removeAnniversary(item.id)">
                  <template #reference>
                    <button class="danger">删除</button>
                  </template>
                </el-popconfirm>
              </div>
            </div>
            <div v-if="!loadingAnni && !anniversaryList.length" class="sv2-empty">还没有纪念日</div>
          </div>
        </section>

        <!-- Whispers -->
        <section v-show="!isCompactMobile || mobileSection === 'whisper'" class="sv2-wh-section">
          <div class="sv2-wh-head">
            <div>
              <h2><span class="material-symbols-outlined">mail</span> 悄悄话</h2>
              <p>留给Ta的小信件</p>
            </div>
            <button class="sv2-wh-compose" @click="openWhisperDialog()">
              <span class="material-symbols-outlined" style="font-size:16px">edit</span> 写信
            </button>
          </div>
          <div v-loading="loadingWhispers" class="sv2-wh-body">
            <div v-if="sentWhispers.length" class="sv2-wh-list">
              <div
                v-for="item in sentWhispers"
                :key="`sent-${item.id}`"
                class="sv2-letter"
              >
                <div class="sv2-letter__flap"></div>
                <div class="sv2-letter__body">
                  <div class="sv2-letter__top">
                    <span class="material-symbols-outlined sv2-letter__icon">mail</span>
                    <span class="sv2-letter__badge" :class="{
                      'sv2-letter__badge--now': item.visibleType === 'immediately',
                      'sv2-letter__badge--wait': item.visibleType === 'scheduled',
                      'sv2-letter__badge--lock': item.visibleType === 'never'
                    }">
                      {{ item.visibleType === 'immediately' ? '已送达' : item.visibleType === 'scheduled' ? '定时' : '私密' }}
                    </span>
                  </div>
                  <p class="sv2-letter__text">{{ item.content }}</p>
                  <div v-if="item.visibleTime" class="sv2-letter__meta">
                    <time class="sv2-letter__time sv2-letter__time--accent">
                      <span class="material-symbols-outlined" style="font-size:13px">schedule_send</span>
                      定时发送：{{ formatDateTimeCn(item.visibleTime) }}
                    </time>
                  </div>
                  <div class="sv2-letter__footer">
                    <time class="sv2-letter__time">
                      <span class="material-symbols-outlined" style="font-size:13px">schedule</span>
                      {{ formatDateTimeCn(item.createTime) }}
                    </time>
                    <div class="sv2-letter__actions">
                      <button
                        v-if="item.visibleType !== 'immediately'"
                        @click="releaseWhisper(item.id)"
                      >
                        <span class="material-symbols-outlined" style="font-size:14px">send</span> 放送
                      </button>
                      <el-popconfirm title="确认删除？" @confirm="removeWhisper(item.id)">
                        <template #reference>
                          <button class="danger">
                            <span class="material-symbols-outlined" style="font-size:14px">delete</span>
                          </button>
                        </template>
                      </el-popconfirm>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="sv2-wh-empty">
              <span class="material-symbols-outlined" style="font-size:40px;color:var(--sv-outline-variant)">drafts</span>
              <p>还没有写过悄悄话</p>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Back to top -->
    <button v-show="showBackToTop" class="sv2-back-top" @click="scrollToTop">
      <span class="material-symbols-outlined">arrow_upward</span>
    </button>

    <el-dialog v-model="editorVisible" width="min(92vw, 760px)" class="pv4-dialog" destroy-on-close append-to-body>
      <div class="pv4-dialog-content">
        <div class="sv-dialog-top">
          <h3 class="pv4-dialog-title">{{ editorMode === 'create' ? '新增记忆' : '编辑记忆' }}</h3>
          <button class="sv-dialog-close" @click="closeEditorDialog">×</button>
        </div>

        <div class="pv4-dialog-form">
          <div class="pv4-form-grid">
            <div class="pv4-field">
              <label>标题</label>
              <el-input v-model="storyForm.title" placeholder="给这段记忆起个名字" />
            </div>
            <div class="pv4-field">
              <label>时间</label>
              <el-date-picker
                v-model="storyForm.happenTime"
                type="date"
                value-format="YYYY-MM-DD"
                format="YYYY / MM / DD"
                placeholder="选择日期"
                popper-class="pv4-date-popper"
                style="width: 100%;"
              />
            </div>
            <div class="pv4-field">
              <label>地点</label>
              <el-input v-model="storyForm.location" placeholder="在哪里发生的" />
            </div>
            <div class="pv4-field">
              <label>访客可见</label>
              <el-switch
                :model-value="storyForm.visitorVisible === 1"
                @change="(v) => storyForm.visitorVisible = v ? 1 : 0"
                inline-prompt active-text="是" inactive-text="否"
              />
            </div>
            <div class="pv4-field">
              <label>心情</label>
              <el-input v-model="storyForm.mood" placeholder="当时的心情" />
            </div>
            <div class="pv4-field">
              <label>天气</label>
              <el-input v-model="storyForm.weather" placeholder="那天的天气" />
            </div>
            <div class="pv4-field pv4-field--full">
              <label>内容</label>
              <el-input v-model="storyForm.description" type="textarea" :rows="4" placeholder="记录这段故事…" resize="none" />
            </div>
          </div>

          <input ref="mediaInput" class="pv4-hidden-input" type="file" multiple @change="handleMediaChange">

          <div class="sv-media-preview-grid">
            <div v-for="(media, index) in storyForm.mediaList" :key="media.url + index" class="sv-media-preview-card">
              <div class="sv-media-preview-thumb">
                <img v-if="media.type === 'image'" :src="media.url" alt="preview">
                <video v-else-if="media.type === 'video'" :src="media.url" preload="metadata"></video>
                <div v-else class="sv-media-preview-fallback">
                  <span class="material-symbols-outlined" style="font-size:28px">{{ media.type === 'audio' ? 'audio_file' : 'attach_file' }}</span>
                  <span>{{ media.fileName || (media.type === 'audio' ? '音频' : '文件') }}</span>
                </div>
                <div class="sv-media-preview-overlay">
                  <el-button circle size="small" type="danger" @click="removeMedia(index)">
                    <span style="font-size: 14px; font-weight: 700;">×</span>
                  </el-button>
                </div>
              </div>
              <div class="sv-media-preview-alias">
                <input v-model="media.alias" class="sv-alias-input" placeholder="备注别名">
              </div>
            </div>

            <div class="sv-media-preview-card sv-media-add-card" @click="triggerMediaUpload">
              <div class="sv-media-add-inner" v-loading="uploadingMedia">
                <span class="sv-media-add-icon">+</span>
              </div>
            </div>
          </div>
        </div>

        <div class="pv4-dialog-footer">
          <el-button round @click="closeEditorDialog">取消</el-button>
          <el-button type="danger" round :loading="savingStory" @click="submitStory">保存</el-button>
        </div>
      </div>
    </el-dialog>

    <el-dialog v-model="detailVisible" width="min(92vw, 760px)" class="pv4-dialog" destroy-on-close append-to-body>
      <div class="pv4-dialog-content" v-loading="loadingDetail">
        <template v-if="storyDetail.story">
          <div class="sv-detail-head">
            <div>
              <h3 class="pv4-dialog-title">{{ storyDetail.story.title }}</h3>
              <p class="pv4-dialog-desc" style="margin-bottom: 0;">{{ formatDateCn(storyDetail.story.happenTime) }}<template v-if="storyDetail.story.location"> · {{ storyDetail.story.location }}</template></p>
            </div>
            <div class="sv-detail-actions">
              <div v-if="!isVisitor" class="sv-detail-actions__group">
                <button class="sv-detail-action-btn" @click="detailVisible = false; openEditDialog(storyDetail.story.id)">编辑</button>
                <el-popconfirm title="确认删除？" @confirm="detailVisible = false; removeStory(storyDetail.story.id)">
                  <template #reference>
                    <button class="sv-detail-action-btn sv-detail-action-btn--danger">删除</button>
                  </template>
                </el-popconfirm>
              </div>
              <button class="sv-dialog-close sv-detail-actions__close" @click="detailVisible = false">×</button>
            </div>
          </div>

          <p v-if="storyDetail.story.description" class="sv-detail-desc">{{ storyDetail.story.description }}</p>

          <div v-if="storyDetail.story.mood || storyDetail.story.weather" class="sv-detail-tags">
            <span v-if="storyDetail.story.mood" class="sv-timeline-card__mood">{{ storyDetail.story.mood }}</span>
            <span v-if="storyDetail.story.weather" class="sv-timeline-card__weather">{{ storyDetail.story.weather }}</span>
          </div>

          <div v-if="storyDetail.mediaList.length" class="sv-media-grid">
            <div v-for="media in storyDetail.mediaList" :key="media.id || media.url" class="sv-media-item" @click.stop="(media.type === 'image' || media.type === 'video') && openLightbox(media.url, media.type)">
              <img v-if="media.type === 'image'" :src="media.url" alt="">
              <video v-else-if="media.type === 'video'" :src="media.url" controls preload="metadata"></video>
              <audio v-else-if="media.type === 'audio'" :src="media.url" controls preload="metadata"></audio>
              <div v-else class="sv-media-item__file">
                <span class="material-symbols-outlined" style="font-size:32px">attach_file</span>
                <a :href="media.url" target="_blank" @click.stop>{{ media.alias || media.fileName || '下载文件' }}</a>
              </div>
              <div v-if="media.alias" class="sv-media-item__alias">{{ media.alias }}</div>
            </div>
          </div>

          <div class="sv-comment-section">
            <div v-if="storyDetail.comments.length" class="sv-comment-list">
              <div v-for="comment in storyDetail.comments" :key="comment.id" class="sv-comment-item">
                <img :src="comment.avatar || 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'" class="sv-comment-avatar" alt="">
                <div class="sv-comment-body">
                  <div class="sv-comment-header">
                    <span class="sv-comment-author">{{ comment.nickname || '用户' }}</span>
                    <span class="sv-comment-time">{{ formatDateTimeCn(comment.createTime) }}</span>
                  </div>
                  <p class="sv-comment-text">{{ comment.content }}</p>
                </div>
                <el-popconfirm v-if="!isVisitor" title="确认删除？" @confirm="removeComment(comment.id)">
                  <template #reference>
                    <el-button text type="danger" size="small" class="sv-comment-del">删除</el-button>
                  </template>
                </el-popconfirm>
              </div>
            </div>

            <div class="sv-comment-compose">
              <el-input v-model="commentForm.content" type="textarea" :rows="2" placeholder="说点什么…" resize="none" />
              <el-button type="danger" round size="small" :loading="savingComment" @click="submitComment">发送</el-button>
            </div>
          </div>
        </template>
      </div>
    </el-dialog>

    <Teleport to="body">
      <div v-if="lightboxUrl" ref="lightboxRef" class="sv-lightbox-overlay" @click="closeLightbox">
        <img v-if="lightboxType === 'image'" :src="lightboxUrl" class="sv-lightbox-img" @click.stop>
        <video v-else-if="lightboxType === 'video'" :src="lightboxUrl" class="sv-lightbox-img" controls autoplay playsinline @click.stop></video>
        <button class="sv-lightbox-close" @click="closeLightbox">×</button>
      </div>
    </Teleport>

    <el-dialog v-model="anniDialogVisible" width="min(88vw, 420px)" class="pv4-dialog sv-lite-dialog" destroy-on-close append-to-body>
      <div class="pv4-dialog-content sv-lite-dialog__content">
        <div class="sv-dialog-top">
          <h3 class="pv4-dialog-title">{{ anniForm.id ? '编辑纪念日' : '新增纪念日' }}</h3>
          <button class="sv-dialog-close" @click="anniDialogVisible = false">×</button>
        </div>
        <div class="pv4-dialog-form sv-lite-form">
          <div class="pv4-field">
            <label>名称</label>
            <el-input v-model="anniForm.title" placeholder="纪念日名称" />
          </div>
          <div class="pv4-field">
            <label>日期</label>
            <el-date-picker
              v-model="anniForm.targetDate"
              type="date"
              value-format="YYYY-MM-DD"
              format="YYYY / MM / DD"
              placeholder="选择日期"
              popper-class="pv4-date-popper"
              style="width: 100%;"
            />
          </div>
          <div class="pv4-field">
            <label>可见性</label>
            <div class="sv-lite-switch-row">
              <el-switch v-model="anniForm.visibility" active-value="shared" inactive-value="private" active-text="共享" inactive-text="私密" />
            </div>
          </div>
          <div class="pv4-field">
            <label>重复方式</label>
            <el-select
              v-model="anniForm.repeatType"
              class="sv-anni-select"
              placeholder="选择重复方式"
              popper-class="sv-anni-select-popper"
              teleported
              fit-input-width
              style="width: 100%;"
            >
              <el-option label="每年循环" value="yearly" />
              <el-option label="每月循环" value="monthly" />
              <el-option label="不循环" value="none" />
            </el-select>
          </div>
        </div>
        <div class="pv4-dialog-footer sv-lite-dialog__footer">
          <el-button round @click="anniDialogVisible = false">取消</el-button>
          <el-button type="danger" round :loading="savingAnni" @click="submitAnniversary">保存</el-button>
        </div>
      </div>
    </el-dialog>

    <el-dialog v-model="whisperDialogVisible" width="min(88vw, 460px)" class="pv4-dialog sv-lite-dialog" destroy-on-close append-to-body>
      <div class="pv4-dialog-content sv-lite-dialog__content">
        <div class="sv-dialog-top">
          <h3 class="pv4-dialog-title">写一条悄悄话</h3>
          <button class="sv-dialog-close" @click="whisperDialogVisible = false">×</button>
        </div>
        <div class="pv4-dialog-form sv-lite-form">
          <div class="pv4-field">
            <label>内容</label>
            <el-input v-model="whisperForm.content" type="textarea" :rows="4" placeholder="写点只想悄悄告诉对方的话…" resize="none" />
          </div>
          <div class="pv4-field">
            <label>可见方式</label>
            <el-select
              v-model="whisperForm.visibleType"
              class="sv-anni-select"
              placeholder="选择可见方式"
              popper-class="sv-anni-select-popper"
              teleported
              fit-input-width
              style="width: 100%;"
            >
              <el-option label="立即可见" value="immediately" />
              <el-option label="仅我一人可见" value="never" />
              <el-option label="定时放送" value="scheduled" />
            </el-select>
          </div>
          <div v-if="whisperForm.visibleType === 'scheduled'" class="pv4-form-grid">
            <div class="pv4-field">
              <label>放送日期</label>
              <el-date-picker
                v-model="whisperForm.visibleDate"
                type="date"
                value-format="YYYY-MM-DD"
                format="YYYY年MM月DD日"
                placeholder="选择日期"
                popper-class="pv4-date-popper"
                style="width: 100%;"
              />
            </div>
            <div class="pv4-field">
              <label>放送时间</label>
              <el-time-picker
                v-model="whisperForm.visibleClock"
                value-format="HH:mm:ss"
                format="HH:mm:ss"
                placeholder="选择时间"
                style="width: 100%;"
              />
            </div>
          </div>
        </div>
        <div class="pv4-dialog-footer sv-lite-dialog__footer">
          <el-button round @click="whisperDialogVisible = false">取消</el-button>
          <el-button type="danger" round :loading="savingWhisper" @click="submitWhisper">发送</el-button>
        </div>
      </div>
    </el-dialog>

  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import {
  createStory,
  createStoryComment,
  deleteStory,
  deleteStoryComment,
  getStoryDetail,
  getStoryPage,
  getVisitorStories,
  getVisitorStoryDetail,
  updateStory,
  uploadStoryFiles
} from '../api/story'
import {
  getAnniversaryList,
  createAnniversary,
  updateAnniversary,
  deleteAnniversary
} from '../api/anniversary'
import {
  createMessage,
  getSentMessages,
  releaseMessage,
  deleteMessage
} from '../api/message'

gsap.registerPlugin(ScrollTrigger)

const timelineRef = ref(null)
const loadMoreRef = ref(null)
const sidebarStickyRef = ref(null)
let loadMoreObserver = null
const loadingStories = ref(false)
const savingStory = ref(false)
const uploadingMedia = ref(false)
const editorVisible = ref(false)
const detailVisible = ref(false)
const loadingDetail = ref(false)
const savingComment = ref(false)
const editorMode = ref('create')
const editingStoryId = ref(null)

const stories = ref([])
const pagination = reactive({ pageNum: 1, pageSize: 20, total: 0 })
const storyDetail = reactive({ story: null, mediaList: [], comments: [] })
const commentForm = reactive({ storyId: null, content: '' })
const loadingAnni = ref(false)
const savingAnni = ref(false)
const anniDialogVisible = ref(false)
const anniversaryList = ref([])
const anniForm = reactive({ id: null, title: '', targetDate: '', repeatType: 'yearly', visibility: 'shared' })
const loadingWhispers = ref(false)
const savingWhisper = ref(false)
const whisperDialogVisible = ref(false)
const sentWhispers = ref([])
const whisperForm = reactive({ content: '', visibleType: 'immediately', visibleDate: '', visibleClock: '20:00:00' })
const isMobileView = ref(false)
const mobileSection = ref('timeline')
const showBackToTop = ref(false)
const mediaInput = ref(null)

const storyForm = reactive({
  title: '',
  happenTime: '',
  location: '',
  description: '',
  mood: '',
  weather: '',
  visitorVisible: 1,
  mediaList: []
})

const inferMediaType = (url = '') => /\.(mp4|mov|avi|webm|m4v)(\?.*)?$/i.test(url) ? 'video' : 'image'

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('us_space_user') || 'null')
  } catch {
    return null
  }
}

const syncViewportState = () => {
  isMobileView.value = window.innerWidth <= 768
  if (!isMobileView.value) {
    mobileSection.value = 'timeline'
  }
}

const handleWindowScroll = () => {
  showBackToTop.value = window.scrollY > 280
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const storedUser = computed(() => getStoredUser())
const currentUserId = computed(() => Number(storedUser.value?.id || storedUser.value?.userId || 0) || null)
const isVisitor = computed(() => storedUser.value?.role === 'visitor')
const isCompactMobile = computed(() => isMobileView.value)
const inferredPartnerUserId = computed(() => {
  const user = storedUser.value || {}
  const candidate = Number(user.partnerUserId || user.targetUserId || user.otherUserId || user.loverUserId || 0)
  if (candidate && candidate !== currentUserId.value) return candidate
  if (user.role === 'admin') return currentUserId.value === 2 ? null : 2
  if (user.role === 'partner') return currentUserId.value === 1 ? null : 1
  return null
})

const formatDateCn = (value) => {
  if (!value) return ''
  const d = String(value).slice(0, 10)
  const [y, m, dd] = d.split('-')
  return `${y}年${m}月${dd}日`
}

const formatDateTimeCn = (value) => {
  if (!value) return ''
  const s = String(value).replace('T', ' ')
  const d = s.slice(0, 10)
  const t = s.slice(11, 16)
  const [y, m, dd] = d.split('-')
  return `${y}年${m}月${dd}日 ${t}`
}

const normalizeStoryDetail = (data) => {
  if (!data || typeof data !== 'object') {
    return { story: null, mediaList: [], comments: [] }
  }

  if (data.story) {
    return {
      story: data.story || null,
      mediaList: Array.isArray(data.mediaList) ? data.mediaList : [],
      comments: Array.isArray(data.comments) ? data.comments : []
    }
  }

  const { comments, mediaList, ...story } = data
  return {
    story: story?.id ? story : null,
    mediaList: Array.isArray(mediaList) ? mediaList : [],
    comments: Array.isArray(comments) ? comments : []
  }
}

const anniversaryWeekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']

const parseDateOnly = (value) => {
  if (!value) return null
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return null
  const date = new Date(y, m - 1, d)
  date.setHours(0, 0, 0, 0)
  return date
}

const getMonthLastDay = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate()

const createSafeDate = (year, monthIndex, day) => {
  const date = new Date(year, monthIndex, Math.min(day, getMonthLastDay(year, monthIndex)))
  date.setHours(0, 0, 0, 0)
  return date
}

const getDateDiffDays = (from, to) => Math.round((to.getTime() - from.getTime()) / 86400000)

const resolveRepeatType = (item) => item.repeatType || (item.isAnnual === 0 ? 'none' : 'yearly')

const getRepeatTypeLabel = (repeatType) => ({
  yearly: '每年循环',
  monthly: '每月循环',
  none: '不循环'
}[repeatType] || '每年循环')

const formatAnniversaryStartDate = (value) => {
  const date = parseDateOnly(value)
  if (!date) return String(value || '')
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${anniversaryWeekdays[date.getDay()]}`
}

const buildAnniversaryDisplay = (item) => {
  const repeatType = resolveRepeatType(item)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const startDate = parseDateOnly(item.targetDate)

  if (!startDate) {
    return {
      prefix: '还有',
      days: item.daysLeft ?? '--',
      tone: 'upcoming',
      startDateText: String(item.targetDate || '')
    }
  }

  if (repeatType === 'none') {
    const diff = getDateDiffDays(today, startDate)
    if (diff < 0) {
      return {
        prefix: '已经',
        days: Math.abs(diff),
        tone: 'passed',
        startDateText: formatAnniversaryStartDate(item.targetDate)
      }
    }
    return {
      prefix: diff === 0 ? '就是今天' : '还有',
      days: diff,
      tone: diff === 0 ? 'today' : 'upcoming',
      startDateText: formatAnniversaryStartDate(item.targetDate)
    }
  }

  const diff = Number.isFinite(Number(item.daysLeft)) ? Number(item.daysLeft) : (() => {
    let nextDate = startDate
    if (repeatType === 'monthly') {
      nextDate = createSafeDate(today.getFullYear(), today.getMonth(), startDate.getDate())
      if (nextDate < today) {
        const nextMonthIndex = today.getMonth() === 11 ? 0 : today.getMonth() + 1
        const nextYear = today.getMonth() === 11 ? today.getFullYear() + 1 : today.getFullYear()
        nextDate = createSafeDate(nextYear, nextMonthIndex, startDate.getDate())
      }
    } else {
      nextDate = createSafeDate(today.getFullYear(), startDate.getMonth(), startDate.getDate())
      if (nextDate < today) {
        nextDate = createSafeDate(today.getFullYear() + 1, startDate.getMonth(), startDate.getDate())
      }
    }
    return getDateDiffDays(today, nextDate)
  })()

  return {
    prefix: diff === 0 ? '就是今天' : '还有',
    days: diff,
    tone: diff === 0 ? 'today' : 'upcoming',
    startDateText: formatAnniversaryStartDate(item.targetDate)
  }
}

const normalizeWhisperList = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.records)) return payload.records
  if (Array.isArray(payload?.list)) return payload.list
  if (Array.isArray(payload?.items)) return payload.items
  return []
}

const normalizeAnniversaryDate = (value) => {
  if (!value) return ''
  return String(value).slice(0, 10)
}

const resolveAnniversaryVisibility = (item) => {
  if (item?.visibility === 'private' || item?.visibility === 'shared') return item.visibility
  if (item?.isPrivate === 1 || item?.privateVisible === 1) return 'private'
  return 'shared'
}

const normalizeAnniversary = (item) => {
  const base = item?.anniversary && typeof item.anniversary === 'object'
    ? { ...item, ...item.anniversary }
    : item
  const repeatType = resolveRepeatType(base)
  return {
    ...base,
    targetDate: normalizeAnniversaryDate(base?.targetDate || base?.anniversaryDate || base?.date),
    visibility: resolveAnniversaryVisibility(base),
    repeatType,
    repeatLabel: getRepeatTypeLabel(repeatType),
    display: buildAnniversaryDisplay({ ...base, repeatType })
  }
}

const groupedStories = computed(() => {
  const groups = []
  const map = new Map()
  stories.value.forEach((story) => {
    const dateKey = String(story.happenTime || '').slice(0, 10)
    if (!map.has(dateKey)) {
      const group = { date: dateKey, stories: [] }
      map.set(dateKey, group)
      groups.push(group)
    }
    map.get(dateKey).stories.push(story)
  })
  return groups
})

const lightboxUrl = ref('')
const lightboxType = ref('image')
const lightboxRef = ref(null)

const openLightbox = async (url, type = 'image') => {
  lightboxUrl.value = url
  lightboxType.value = type
  await nextTick()
  const overlay = lightboxRef.value
  if (!overlay) return
  const img = overlay.querySelector('.sv-lightbox-img')
  const closeBtn = overlay.querySelector('.sv-lightbox-close')

  const tl = gsap.timeline()
  tl.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' })
  tl.fromTo(img,
    { scale: 0.3, opacity: 0 },
    { scale: 1, opacity: 1, duration: 0.6, ease: 'elastic.out(1, 0.5)' },
    '-=0.3'
  )
  tl.fromTo(closeBtn,
    { opacity: 0, y: -10 },
    { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' },
    '-=0.3'
  )

  const handleEsc = (e) => {
    if (e.key === 'Escape') { closeLightbox(); document.removeEventListener('keydown', handleEsc) }
  }
  document.addEventListener('keydown', handleEsc)
}

const closeLightbox = () => {
  const overlay = lightboxRef.value
  if (!overlay) { lightboxUrl.value = ''; return }
  const img = overlay.querySelector('.sv-lightbox-img')

  const tl = gsap.timeline({ onComplete: () => { lightboxUrl.value = '' } })
  tl.to(img, { scale: 0.6, opacity: 0, duration: 0.3, ease: 'power3.in' })
  tl.to(overlay, { opacity: 0, duration: 0.25, ease: 'power2.in' }, '-=0.15')
}

const resetComposeForm = () => {
  editingStoryId.value = null
  editorMode.value = 'create'
  storyForm.title = ''
  storyForm.happenTime = ''
  storyForm.location = ''
  storyForm.description = ''
  storyForm.mood = ''
  storyForm.weather = ''
  storyForm.visitorVisible = 1
  storyForm.mediaList = []
}

const closeEditorDialog = () => {
  editorVisible.value = false
  resetComposeForm()
}

const openCreateDialog = () => {
  resetComposeForm()
  editorVisible.value = true
}

const buildPayload = () => ({
  title: storyForm.title.trim(),
  happenTime: storyForm.happenTime ? storyForm.happenTime + ' 00:00:00' : '',
  location: storyForm.location.trim() || undefined,
  description: storyForm.description.trim() || undefined,
  mood: storyForm.mood.trim() || undefined,
  weather: storyForm.weather.trim() || undefined,
  visitorVisible: storyForm.visitorVisible,
  mediaList: storyForm.mediaList.length
    ? storyForm.mediaList
        .map((item, index) => ({
          type: item.type,
          url: item.url,
          alias: item.alias?.trim() || undefined,
          sort: Number(item.sort) || index + 1
        }))
        .sort((a, b) => a.sort - b.sort)
    : undefined
})

const fetchStories = async () => {
  loadingStories.value = true
  try {
    const fetcher = isVisitor.value ? getVisitorStories : getStoryPage
    const data = await fetcher({ pageNum: pagination.pageNum, pageSize: pagination.pageSize })
    stories.value = data.records || []
    pagination.total = data.total || 0
    pagination.pageNum = data.current || pagination.pageNum
    pagination.pageSize = data.size || pagination.pageSize
  } catch (error) {
    ElMessage.error(error.message || '加载失败')
  } finally {
    loadingStories.value = false
  }
}

/* ── 纪念日 ── */
const fetchAnniversaryList = async () => {
  loadingAnni.value = true
  try {
    const all = await getAnniversaryList() || []
    anniversaryList.value = all.filter(item => !item.isSystem).map(normalizeAnniversary)
  } catch (error) {
    ElMessage.error(error.message || '加载纪念日失败')
  } finally {
    loadingAnni.value = false
  }
}

const openAnniDialog = (item) => {
  if (item) {
    const normalized = normalizeAnniversary(item)
    anniForm.id = normalized.id
    anniForm.title = normalized.title || ''
    anniForm.targetDate = normalized.targetDate
    anniForm.repeatType = normalized.repeatType
    anniForm.visibility = normalized.visibility
  } else {
    anniForm.id = null
    anniForm.title = ''
    anniForm.targetDate = ''
    anniForm.repeatType = 'yearly'
    anniForm.visibility = 'shared'
  }
  anniDialogVisible.value = true
}

const submitAnniversary = async () => {
  if (!anniForm.title?.trim()) return ElMessage.warning('请输入纪念日名称')
  if (!anniForm.targetDate) return ElMessage.warning('请选择日期')
  savingAnni.value = true
  try {
    const payload = {
      title: anniForm.title.trim(),
      targetDate: anniForm.targetDate,
      repeatType: anniForm.repeatType,
      visibility: anniForm.visibility
    }
    if (anniForm.id) {
      await updateAnniversary(anniForm.id, payload)
    } else {
      await createAnniversary(payload)
    }
    anniDialogVisible.value = false
    await fetchAnniversaryList()
  } catch (error) {
    ElMessage.error(error.message || '保存失败')
  } finally {
    savingAnni.value = false
  }
}

const removeAnniversary = async (id) => {
  try {
    await deleteAnniversary(id)
    await fetchAnniversaryList()
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  }
}

const resetWhisperForm = () => {
  whisperForm.content = ''
  whisperForm.visibleType = 'immediately'
  whisperForm.visibleDate = ''
  whisperForm.visibleClock = '20:00:00'
}

const fetchWhispers = async () => {
  loadingWhispers.value = true
  try {
    const sent = await getSentMessages({ pageNum: 1, pageSize: 20 })
    sentWhispers.value = normalizeWhisperList(sent)
  } catch (error) {
    ElMessage.error(error.message || '加载悄悄话失败')
  } finally {
    loadingWhispers.value = false
  }
}

const openWhisperDialog = () => {
  resetWhisperForm()
  whisperDialogVisible.value = true
}

const submitWhisper = async () => {
  if (!inferredPartnerUserId.value) return ElMessage.warning('暂时识别不到对方账号，无法发送悄悄话')
  if (!whisperForm.content?.trim()) return ElMessage.warning('请输入悄悄话内容')
  if (whisperForm.visibleType === 'scheduled' && (!whisperForm.visibleDate || !whisperForm.visibleClock)) {
    return ElMessage.warning('请选择完整的放送日期和时间')
  }
  savingWhisper.value = true
  try {
    const payload = {
      toUserId: inferredPartnerUserId.value,
      content: whisperForm.content.trim(),
      visibleType: whisperForm.visibleType,
      visibleTime: whisperForm.visibleType === 'scheduled'
        ? `${whisperForm.visibleDate} ${whisperForm.visibleClock}`
        : undefined
    }
    await createMessage(payload)
    ElMessage.success('悄悄话已写好')
    whisperDialogVisible.value = false
    resetWhisperForm()
    await fetchWhispers()
  } catch (error) {
    ElMessage.error(error.message || '发送悄悄话失败')
  } finally {
    savingWhisper.value = false
  }
}

const releaseWhisper = async (id) => {
  try {
    const result = await releaseMessage(id)
    ElMessage.success(result?.visibleType === 'immediately' ? '已立即放送给对方' : '放送成功')
    await fetchWhispers()
  } catch (error) {
    ElMessage.error(error.message || '放送失败')
  }
}

const removeWhisper = async (id) => {
  try {
    await deleteMessage(id)
    ElMessage.success('已删除悄悄话')
    await fetchWhispers()
  } catch (error) {
    ElMessage.error(error.message || '删除悄悄话失败')
  }
}

const openStoryDetail = async (id) => {
  detailVisible.value = true
  loadingDetail.value = true
  commentForm.storyId = id
  commentForm.content = ''
  try {
    const detailFetcher = isVisitor.value ? getVisitorStoryDetail : getStoryDetail
    const data = await detailFetcher(id)
    const normalized = normalizeStoryDetail(data)
    storyDetail.story = normalized.story
    storyDetail.mediaList = normalized.mediaList
    storyDetail.comments = normalized.comments
  } catch (error) {
    ElMessage.error(error.message || '加载失败')
  } finally {
    loadingDetail.value = false
  }
}

const openEditDialog = async (id) => {
  editorMode.value = 'edit'
  editingStoryId.value = id
  editorVisible.value = true
  try {
    const data = await getStoryDetail(id)
    const normalized = normalizeStoryDetail(data)
    const story = normalized.story || {}
    storyForm.title = story.title || ''
    storyForm.happenTime = story.happenTime ? String(story.happenTime).slice(0, 10) : ''
    storyForm.location = story.location || ''
    storyForm.description = story.description || ''
    storyForm.mood = story.mood || ''
    storyForm.weather = story.weather || ''
    storyForm.visitorVisible = story.visitorVisible === 0 ? 0 : 1
    storyForm.mediaList = normalized.mediaList.map((item, index) => ({
      type: item.type || inferMediaType(item.url),
      url: item.url,
      alias: item.alias || '',
      sort: item.sort || index + 1
    }))
  } catch (error) {
    ElMessage.error(error.message || '加载失败')
  }
}

const submitStory = async () => {
  if (!storyForm.title.trim()) { ElMessage.warning('请输入标题'); return }
  if (!storyForm.happenTime) { ElMessage.warning('请选择时间'); return }
  savingStory.value = true
  try {
    const payload = buildPayload()
    if (editorMode.value === 'edit' && editingStoryId.value) {
      await updateStory(editingStoryId.value, payload)
    } else {
      await createStory(payload)
    }
    closeEditorDialog()
    await fetchStories()
  } catch (error) {
    ElMessage.error(error.message || '保存失败')
  } finally {
    savingStory.value = false
  }
}

const removeStory = async (id) => {
  try {
    await deleteStory(id)
    await fetchStories()
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  }
}

const submitComment = async () => {
  if (!commentForm.storyId) return
  if (!commentForm.content.trim()) { ElMessage.warning('请输入内容'); return }
  savingComment.value = true
  try {
    await createStoryComment({ storyId: commentForm.storyId, content: commentForm.content.trim() })
    commentForm.content = ''
    await openStoryDetail(commentForm.storyId)
  } catch (error) {
    ElMessage.error(error.message || '发送失败')
  } finally {
    savingComment.value = false
  }
}

const removeComment = async (id) => {
  try {
    await deleteStoryComment(id)
    if (commentForm.storyId) await openStoryDetail(commentForm.storyId)
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  }
}

const triggerMediaUpload = () => {
  if (uploadingMedia.value) return
  mediaInput.value?.click()
}

const getFileMediaType = (file) => {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  if (file.type.startsWith('audio/')) return 'audio'
  return 'file'
}

const handleMediaChange = async (event) => {
  const files = Array.from(event.target.files || [])
  if (!files.length) return
  uploadingMedia.value = true
  try {
    const urls = await uploadStoryFiles(files)
    const nextStart = storyForm.mediaList.length + 1
    storyForm.mediaList.push(
      ...urls.map((url, index) => ({
        url,
        type: getFileMediaType(files[index]),
        alias: '',
        fileName: files[index]?.name || '',
        sort: nextStart + index
      }))
    )
  } catch (error) {
    ElMessage.error(error.message || '上传失败')
  } finally {
    event.target.value = ''
    uploadingMedia.value = false
  }
}

const loadMore = async () => {
  pagination.pageNum++
  loadingStories.value = true
  try {
    const fetcher = isVisitor.value ? getVisitorStories : getStoryPage
    const data = await fetcher({ pageNum: pagination.pageNum, pageSize: pagination.pageSize })
    const newRecords = data.records || []
    stories.value = [...stories.value, ...newRecords]
    pagination.total = data.total || 0
  } catch (error) {
    ElMessage.error(error.message || '加载失败')
    pagination.pageNum--
  } finally {
    loadingStories.value = false
  }
}

const animateTimeline = () => {
  if (!timelineRef.value) return

  const items = timelineRef.value.querySelectorAll('.sv2-tl-item')
  items.forEach((item) => {
    const card = item.querySelector('.sv2-tl-card')
    const node = item.querySelector('.sv2-tl-node')

    if (card) gsap.set(card, { opacity: 0, x: -40, y: 16, scale: 0.95 })
    if (node) gsap.set(node, { scale: 0, opacity: 0 })

    ScrollTrigger.create({
      trigger: item,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        const tl = gsap.timeline()
        if (node) tl.to(node, { scale: 1, opacity: 1, duration: 0.35, ease: 'back.out(2)' })
        if (card) tl.to(card, {
          opacity: 1, x: 0, y: 0, scale: 1,
          duration: 0.55, ease: 'power3.out'
        }, '-=0.15')
      }
    })
  })
}

const cleanupTimeline = () => {
  ScrollTrigger.getAll().forEach(st => {
    if (st.trigger && timelineRef.value?.contains(st.trigger)) {
      st.kill()
    }
  })
  const lineTriggers = ScrollTrigger.getAll().filter(st => st.trigger === timelineRef.value)
  lineTriggers.forEach(st => st.kill())
}

const setupSidebarFloat = () => {
  const sidebar = sidebarStickyRef.value
  if (!sidebar || window.innerWidth <= 900) return

  gsap.fromTo(sidebar,
    { y: 0 },
    {
      y: 18,
      ease: 'none',
      scrollTrigger: {
        trigger: document.documentElement,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1.2
      }
    }
  )
}

const cleanupSidebarFloat = () => {
  ScrollTrigger.getAll().forEach(st => {
    if (st.trigger === document.documentElement && st.animation?.targets().includes(sidebarStickyRef.value)) {
      st.kill()
    }
  })
  if (sidebarStickyRef.value) {
    gsap.set(sidebarStickyRef.value, { clearProps: 'transform' })
  }
}

const setupLoadMoreObserver = () => {
  if (loadMoreObserver) loadMoreObserver.disconnect()
  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && !loadingStories.value && pagination.total > stories.value.length) {
        loadMore()
      }
    },
    { rootMargin: '200px' }
  )
}

watch(stories, async () => {
  await nextTick()
  cleanupTimeline()
  animateTimeline()
  if (loadMoreRef.value) {
    setupLoadMoreObserver()
    loadMoreObserver.observe(loadMoreRef.value)
  }
})

watch(loadMoreRef, (el) => {
  if (el) {
    setupLoadMoreObserver()
    loadMoreObserver.observe(el)
  }
})

onMounted(async () => {
  syncViewportState()
  handleWindowScroll()
  window.addEventListener('resize', syncViewportState)
  window.addEventListener('scroll', handleWindowScroll, { passive: true })
  fetchStories()
  if (!isVisitor.value) {
    fetchAnniversaryList()
    fetchWhispers()
  }
  await nextTick()
  setupSidebarFloat()
})

onBeforeUnmount(() => {
  cleanupTimeline()
  cleanupSidebarFloat()
  window.removeEventListener('resize', syncViewportState)
  window.removeEventListener('scroll', handleWindowScroll)
  if (loadMoreObserver) loadMoreObserver.disconnect()
})
</script>

<style scoped>
/* ═══════ Design Tokens ═══════ */
.sv2-page {
  --sv-bg: #fff8f5;
  --sv-surface: #f5ece8;
  --sv-surface-high: #efe6e2;
  --sv-surface-low: #fbf2ed;
  --sv-primary: #7b5455;
  --sv-primary-container: #f4c2c2;
  --sv-secondary: #735c00;
  --sv-on-surface: #1f1b18;
  --sv-on-surface-variant: #504444;
  --sv-outline: #827473;
  --sv-radius: 12px;
  font-family: 'Plus Jakarta Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 40px 64px;
}

/* ═══════ Hero ═══════ */
.sv2-hero {
  text-align: center;
  padding: 32px 0 48px;
}
.sv2-hero h1 {
  font-family: 'Noto Serif', serif;
  font-size: 48px;
  font-weight: 400;
  line-height: 1.2;
  color: var(--sv-on-surface);
  margin: 0 0 8px;
}
.sv2-hero p {
  font-size: 18px;
  line-height: 1.6;
  color: var(--sv-on-surface-variant);
  max-width: 600px;
  margin: 0 auto;
}

/* ═══════ Mobile Tabs ═══════ */
.sv2-mob-tabs {
  display: none;
}

/* ═══════ Grid ═══════ */
.sv2-grid {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
  align-items: start;
}

/* ═══════ Add Button ═══════ */
.sv2-add-row {
  margin-bottom: 24px;
}
.sv2-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  border: none;
  border-radius: 999px;
  background: var(--sv-primary);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(123,84,85,0.25);
}
.sv2-add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(123,84,85,0.35);
}
.sv2-add-btn:active { transform: scale(0.97); }

/* ═══════ Timeline ═══════ */
.sv2-tl-section { min-height: 200px; }
.sv2-tl { position: relative; padding: 8px 0 40px; }
.sv2-tl-line {
  position: absolute;
  left: 15px;
  top: 16px;
  bottom: 32px;
  width: 2px;
  background: rgba(244,194,194,0.5);
  border-radius: 999px;
}
.sv2-tl-item {
  position: relative;
  padding-left: 48px;
  padding-bottom: 32px;
}
.sv2-tl-item:last-child { padding-bottom: 0; }
.sv2-tl-node {
  position: absolute;
  left: 9px;
  top: 6px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2.5px solid var(--sv-secondary);
  background: var(--sv-bg);
  box-shadow: 0 0 10px rgba(115,92,0,0.2);
  transition: transform 0.3s;
  z-index: 1;
}
.sv2-tl-item:hover .sv2-tl-node {
  transform: scale(1.25);
}
.sv2-tl-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sv2-tl-card {
  position: relative;
  overflow: hidden;
  background: var(--sv-surface);
  border-radius: var(--sv-radius);
  padding: 28px 32px;
  border: 1px solid rgba(255,255,255,0.6);
  box-shadow: 0 30px 60px -15px rgba(123,84,85,0.06);
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
}
.sv2-tl-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 36px 70px -15px rgba(123,84,85,0.1);
}
.sv2-tl-card__glow {
  position: absolute;
  top: 0;
  right: 0;
  width: 128px;
  height: 128px;
  background: rgba(244,194,194,0.2);
  border-radius: 0 0 0 100%;
  filter: blur(32px);
  pointer-events: none;
}
.sv2-tl-date {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: var(--sv-secondary);
  background: var(--sv-surface-high);
  padding: 4px 12px;
  border-radius: 999px;
  margin-bottom: 12px;
}
.sv2-tl-title {
  font-family: 'Noto Serif', serif;
  font-size: 28px;
  font-weight: 400;
  line-height: 1.3;
  color: var(--sv-on-surface);
  margin: 0 0 8px;
}
.sv2-tl-desc {
  font-size: 16px;
  line-height: 1.6;
  color: var(--sv-on-surface-variant);
  margin: 0 0 16px;
}
.sv2-tl-media {
  margin-top: 4px;
  border-radius: 8px;
  overflow: hidden;
}
.sv2-tl-media img {
  width: 100%;
  height: auto;
  max-height: 280px;
  object-fit: cover;
  display: block;
  border-radius: 8px;
  border: 1px solid rgba(0,0,0,0.05);
  transition: transform 0.5s;
}
.sv2-tl-card:hover .sv2-tl-media img {
  transform: scale(1.03);
}
.sv2-tl-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}
.sv2-tag {
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 999px;
}
.sv2-tag--loc { background: rgba(123,84,85,0.06); color: var(--sv-primary); }
.sv2-tag--mood { background: rgba(233,92,138,0.08); color: #d84b72; }
.sv2-tag--weather { background: rgba(100,160,230,0.08); color: #5a8cc0; }

/* ═══════ Load more ═══════ */
.sv2-load-more {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 32px 0;
}
.sv2-load-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--sv-primary-container);
  animation: sv2Bounce 1.2s ease-in-out infinite;
}
.sv2-load-dot:nth-child(2) { animation-delay: 0.15s; }
.sv2-load-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes sv2Bounce {
  0%,80%,100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1.2); opacity: 1; }
}
.sv2-load-end {
  text-align: center;
  padding: 24px 0;
  font-size: 13px;
  color: var(--sv-outline);
}

/* ═══════ Sidebar Column ═══════ */
.sv2-sidebar-col {
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: sticky;
  top: 88px;
}

/* ═══════ Milestones ═══════ */
.sv2-ms-section {
  background: #fff;
  border-radius: var(--sv-radius);
  padding: 24px;
  border: 1px solid var(--sv-surface-high);
  box-shadow: 0 20px 50px -10px rgba(123,84,85,0.05);
}
.sv2-ms-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.sv2-ms-head h2 {
  font-family: 'Noto Serif', serif;
  font-size: 22px;
  font-weight: 500;
  color: var(--sv-on-surface);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sv2-ms-head h2 .material-symbols-outlined {
  color: var(--sv-primary);
  font-size: 22px;
}
.sv2-ms-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--sv-surface-high);
  background: var(--sv-surface-low);
  color: var(--sv-on-surface-variant);
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s;
}
.sv2-ms-add:hover {
  background: var(--sv-primary-container);
  border-color: var(--sv-primary-container);
  color: var(--sv-primary);
}
.sv2-ms-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sv2-ms-card {
  position: relative;
  overflow: hidden;
  background: var(--sv-surface);
  border: 1px solid var(--sv-surface-high);
  border-radius: 8px;
  padding: 20px;
  transition: transform 0.25s;
}
.sv2-ms-card:hover { transform: translateY(-2px); }
.sv2-ms-card--primary {
  background: rgba(123,84,85,0.05);
  border-color: rgba(123,84,85,0.1);
}
.sv2-ms-card__icon {
  position: absolute;
  right: -4px;
  bottom: -4px;
  opacity: 0.08;
  transition: transform 0.5s;
}
.sv2-ms-card:hover .sv2-ms-card__icon { transform: scale(1.1); }
.sv2-ms-card__icon .material-symbols-outlined {
  font-size: 80px;
  font-variation-settings: 'FILL' 1;
}
.sv2-ms-card--primary .sv2-ms-card__icon { opacity: 0.1; }
.sv2-ms-card__label {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--sv-on-surface-variant);
  margin: 0 0 4px;
}
.sv2-ms-card__count {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.sv2-ms-card__num {
  font-family: 'Noto Serif', serif;
  font-size: 42px;
  font-weight: 400;
  line-height: 1;
  color: var(--sv-on-surface);
}
.sv2-ms-card--primary .sv2-ms-card__num { color: var(--sv-primary); }
.sv2-ms-card__unit {
  font-size: 16px;
  color: var(--sv-on-surface-variant);
}
.sv2-ms-card--primary .sv2-ms-card__unit { color: rgba(123,84,85,0.8); }
.sv2-ms-card__date {
  font-size: 14px;
  color: var(--sv-on-surface-variant);
  margin: 8px 0 0;
}
.sv2-ms-card__actions {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}
.sv2-ms-card__actions button {
  border: 1px solid var(--sv-surface-high);
  background: transparent;
  color: var(--sv-on-surface-variant);
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s;
}
.sv2-ms-card__actions button:hover {
  background: var(--sv-surface-high);
}
.sv2-ms-card__actions button.danger {
  color: #ba1a1a;
}
.sv2-ms-card__actions button.danger:hover {
  background: rgba(186,26,26,0.08);
  border-color: rgba(186,26,26,0.2);
}

/* ═══════ Whispers (envelope/card) ═══════ */
.sv2-wh-section {
  background: #fff;
  border-radius: var(--sv-radius);
  border: 1px solid rgba(212,194,194,0.3);
  box-shadow: 0 20px 40px rgba(123,84,85,0.05);
  display: flex;
  flex-direction: column;
  max-height: 540px;
}
.sv2-wh-head {
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--sv-surface-high);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.sv2-wh-head h2 {
  font-family: 'Noto Serif', serif;
  font-size: 20px;
  font-weight: 500;
  color: var(--sv-on-surface);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sv2-wh-head h2 .material-symbols-outlined {
  color: var(--sv-primary);
  font-size: 22px;
}
.sv2-wh-head p {
  font-size: 13px;
  color: var(--sv-on-surface-variant);
  margin: 3px 0 0;
}
.sv2-wh-compose {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--sv-surface-high);
  background: var(--sv-surface-low);
  color: var(--sv-primary);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  white-space: nowrap;
}
.sv2-wh-compose:hover {
  background: var(--sv-primary-container);
  border-color: var(--sv-primary-container);
}
.sv2-wh-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sv2-wh-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sv2-wh-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 0;
  color: var(--sv-outline);
  font-size: 13px;
}
.sv2-wh-empty p { margin: 0; }

/* ── Letter card ── */
.sv2-letter {
  position: relative;
  background: var(--sv-surface-low);
  border: 1px solid var(--sv-surface-high);
  border-radius: 10px;
  overflow: hidden;
  transition: transform 0.25s, box-shadow 0.25s;
}
.sv2-letter:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(123,84,85,0.08);
}
.sv2-letter__flap {
  height: 6px;
  background: linear-gradient(135deg, var(--sv-primary-container), var(--sv-surface-high));
  border-bottom: 1px solid rgba(212,194,194,0.2);
}
.sv2-letter__body {
  padding: 14px 16px 12px;
}
.sv2-letter__top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.sv2-letter__icon {
  font-size: 18px;
  color: var(--sv-primary);
}
.sv2-letter__badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--sv-surface);
  color: var(--sv-on-surface-variant);
}
.sv2-letter__badge--now {
  background: rgba(123,84,85,0.1);
  color: var(--sv-primary);
}
.sv2-letter__badge--wait {
  background: rgba(180,140,60,0.1);
  color: #8a6d20;
}
.sv2-letter__badge--lock {
  background: rgba(130,116,115,0.1);
  color: var(--sv-outline);
}
.sv2-letter__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
  color: var(--sv-on-surface);
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.sv2-letter__meta {
  margin-top: 8px;
}
.sv2-letter__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(212,194,194,0.15);
}
.sv2-letter__time {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: var(--sv-outline);
}
.sv2-letter__time--accent {
  color: #8a6d20;
  font-weight: 600;
}
.sv2-letter__actions {
  display: flex;
  gap: 6px;
}
.sv2-letter__actions button {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: none;
  background: transparent;
  color: var(--sv-primary);
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s;
  font-family: inherit;
}
.sv2-letter__actions button:hover {
  background: rgba(123,84,85,0.08);
}
.sv2-letter__actions button.danger {
  color: #ba1a1a;
}
.sv2-letter__actions button.danger:hover {
  background: rgba(186,26,26,0.08);
}

/* ═══════ Shared ═══════ */
.sv2-empty {
  text-align: center;
  font-size: 13px;
  color: var(--sv-outline);
  padding: 20px 0;
}
.sv2-back-top {
  position: fixed;
  right: 20px;
  bottom: 28px;
  z-index: 60;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: var(--sv-primary);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(123,84,85,0.3);
  transition: transform 0.2s, box-shadow 0.2s;
}
.sv2-back-top:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(123,84,85,0.4);
}

/* ═══════ Material Symbols helper ═══════ */
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24;
  vertical-align: middle;
}

/* ═══════ Responsive ═══════ */
@media (max-width: 900px) {
  .sv2-grid {
    grid-template-columns: 1fr;
  }
  .sv2-sidebar-col {
    position: static;
  }
}
@media (max-width: 768px) {
  .sv2-page {
    padding: 0 16px 40px;
  }
  .sv2-hero { padding: 20px 0 28px; }
  .sv2-hero h1 { font-size: 32px; }
  .sv2-hero p { font-size: 15px; }
  .sv2-mob-tabs {
    display: flex;
    gap: 8px;
    justify-content: center;
    margin-bottom: 18px;
    padding: 6px;
    background: rgba(255,255,255,0.72);
    border: 1px solid rgba(255,255,255,0.85);
    backdrop-filter: blur(16px);
    border-radius: 16px;
    position: sticky;
    top: 12px;
    z-index: 30;
  }
  .sv2-mob-tabs button {
    flex: 1;
    min-height: 38px;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: var(--sv-on-surface-variant);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }
  .sv2-mob-tabs button.active {
    background: var(--sv-primary);
    color: #fff;
    box-shadow: 0 4px 12px rgba(123,84,85,0.25);
  }
  .sv2-grid { gap: 16px; }
  .sv2-tl-card { padding: 20px; }
  .sv2-tl-title { font-size: 22px; }
  .sv2-tl-desc { font-size: 14px; }
  .sv2-tl-item { padding-left: 36px; padding-bottom: 24px; }
  .sv2-tl-node { left: 7px; width: 12px; height: 12px; }
  .sv2-tl-line { left: 12px; }
  .sv2-wh-section { max-height: none; }
}
@media (max-width: 480px) {
  .sv2-hero h1 { font-size: 28px; }
  .sv2-tl-card { padding: 16px; }
  .sv2-tl-title { font-size: 20px; }
  .sv2-ms-card__num { font-size: 36px; }
}
</style>
